import { useEffect, useRef, useState, type FormEvent } from "react";
import s from "./Library.module.css";
import { useSession } from "../../app/SessionContext";
import { useCreateDocument, useDocumentTypes } from "../../api/queries";
import type { Classification } from "../../domain/library";
import { Button, Field, Input, Select } from "../../design/components";
import { t } from "../../i18n";

/**
 * Create the controlled item. Revisions come afterwards — the platform
 * models a document as the thing that is controlled and a revision as a
 * version of it, and this form keeps that distinction rather than asking
 * for a file up front.
 *
 * Types are the pack's, never typed in: a type the pack has not declared is
 * refused by the platform, so it is not offered here.
 */
export function CreateDocumentDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (documentId: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { site } = useSession();
  const types = useDocumentTypes();
  const create = useCreateDocument();
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [reference, setReference] = useState("");
  const [scope, setScope] = useState<"site" | "all">("site");
  const [classification, setClassification] = useState<"" | Classification>("");
  const [tags, setTags] = useState("");

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup. StrictMode runs this cleanup between its two
    // mounts in development, and the close event it fires unmounted the
    // dialog as it opened — the button did nothing. An unmounted dialog
    // leaves the top layer by itself.
  }, []);

  const chosen = types.data?.find((t) => t.key === type);
  const restricted = chosen?.defaultClassification === "restricted";
  const valid = type.length > 0 && title.trim().length > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    create.mutate(
      {
        type,
        title: title.trim(),
        reference: reference.trim() || undefined,
        siteId: scope === "site" ? site.id : undefined,
        classification: restricted ? "restricted" : classification || undefined,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      },
      { onSuccess: (d) => onCreated(d.id) },
    );
  };

  return (
    <dialog ref={ref} onClose={onClose} style={dialogStyle} aria-label={t("New document")}>
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 15 }}>{t("New document")}</h2>
          <div className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>
            {t(
              "Creates a draft. Add the first revision and file on the document page.",
            )}
          </div>
        </div>

        <Field label="Type (declared by the installed packs)">
          <Select value={type} onChange={(e) => setType(e.target.value)} aria-label={t("Type")} required autoFocus>
            <option value="">{t("Choose a type…")}</option>
            {(types.data ?? []).map((ty) => (
              <option key={ty.key} value={ty.key}>
                {ty.name}
                {ty.controlled ? "" : ` ${t("(uncontrolled)")}`}
              </option>
            ))}
          </Select>
          {chosen && (
            <div className="muted" style={{ fontSize: 13 }}>
              {chosen.description ?? ""}
              {chosen.requiresApproval ? ` ${t("Requires approval before issue.")}` : ""}
              {chosen.reviewMonths ? ` ${t("Reviewed every {n} months.", { n: chosen.reviewMonths })}` : ""}
              {chosen.expires ? ` ${t("Revisions carry an expiry date.")}` : ""}
              {chosen.retentionYears ? ` ${t("Retained {n} years.", { n: chosen.retentionYears })}` : ""}
            </div>
          )}
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} aria-label={t("Title")} required />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Reference (the operator's own number)">
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              aria-label={t("Reference")}
              placeholder={`${site.code}-…`}
            />
          </Field>
          <Field label="Applies at">
            <Select
              value={scope}
              onChange={(e) => setScope(e.target.value as "site" | "all")}
              aria-label={t("Applies at")}
            >
              <option value="site">{t("{code} only", { code: site.code })}</option>
              <option value="all">{t("All sites (tenant-wide)")}</option>
            </Select>
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Classification">
            {restricted ? (
              <div className={s.restricted} style={{ padding: "6px 10px", fontSize: 13 }}>
                {t("Restricted — fixed by the pack for this type.")}
              </div>
            ) : (
              <Select
                value={classification}
                onChange={(e) => setClassification(e.target.value as "" | Classification)}
                aria-label={t("Classification")}
              >
                <option value="">
                  {chosen?.defaultClassification
                    ? t("Pack default ({classification})", { classification: t(chosen.defaultClassification) })
                    : t("Pack default")}
                </option>
                <option value="public">{t("Public")}</option>
                <option value="internal">{t("Internal")}</option>
                <option value="restricted">{t("Restricted")}</option>
              </Select>
            )}
          </Field>
          <Field label="Tags (comma-separated)">
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              aria-label={t("Tags")}
              placeholder={t("runway, friction")}
            />
          </Field>
        </div>

        {create.isError && (
          <div className={s.refusal} role="alert">
            {t("Refused: {reason}", { reason: (create.error as Error).message })}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={create.isPending}>
            {t("Cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending || !valid}>
            {create.isPending ? t("Creating…") : t("Create document")}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

const dialogStyle: React.CSSProperties = {
  background: "var(--panel)",
  color: "var(--ink)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius)",
  padding: 20,
  width: "min(600px, 92vw)",
};
